import React, { useMemo, useRef, useState } from 'react';
import { TablePagination, Table, TableBody, TableCell, TableRow } from '@material-ui/core';
import TableHead from '@material-ui/core/TableHead';
import { withRouter, useHistory, useLocation } from 'react-router';
import { coreApi } from '../../utils/axios';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';

import { Loading } from '../../healtheraComponents';
import styled from 'styled-components';

const typeFilter = [
  'appointment',
  'dependent',
  'order_schedule',
  'organisation',
  'pharmacy',
  'service',
  'surgery',
  'unknown',
  'user',
  'white_label_app'
];

const flattenObject = (obj, delimiter = '.', prefix = '') =>
  Object.keys(obj).reduce((acc, k) => {
    const pre = prefix.length ? `${prefix}${delimiter}` : '';
    if (typeof obj[k] === 'object' && obj[k] !== null && Object.keys(obj[k]).length > 0)
      Object.assign(acc, flattenObject(obj[k], delimiter, pre + k));
    else acc[pre + k] = obj[k];
    return acc;
  }, {});

const payloadOmittedFields = [
  '_id',
  'action',
  'actioned_by',
  'actioned_by_id',
  'full_name',
  'target_id',
  'target_type',
  'timestamp'
];

const AuditLogs = () => {
  const history = useHistory();
  const { search } = useLocation();

  const [selectedAuditLog, setSelectedAuditLog] = useState(null);

  const typeRef = useRef();
  const idRef = useRef();

  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const page = searchParams.get('page') ?? 0;
  const order = searchParams.get('order') ?? 'desc';
  const targetId = searchParams.get('target_id');
  const targetType = searchParams.get('target_type');

  const { data: { data, pagination } = {}, isInitialLoading } = useQuery({
    queryKey: ['auditLogs', page, order, targetId, targetType],
    queryFn: async () => {
      const queryParams = new URLSearchParams({ page, order });
      if (targetId) queryParams.append('targetId', targetId);
      if (targetType) queryParams.append('target', targetType);

      const queryString = queryParams.toString();
      const result = await coreApi.get(`/audit?${queryString}`);
      return result.data;
    }
  });

  function handleChangePage(event, page) {
    searchParams.set('page', page);
    history.replace({ search: searchParams.toString() });
  }

  function handleFilterSubmit() {
    const entityType = typeRef.current.value;
    const entityId = idRef.current.value;

    if (entityType !== '' && entityId !== '') {
      searchParams.set('target_type', entityType);
      searchParams.set('target_id', entityId);
      history.replace({ search: searchParams.toString() });
    }
  }

  function handleClearFilter() {
    searchParams.delete('target_type');
    searchParams.delete('target_id');
    history.replace({ search: searchParams.toString() });
  }

  function snakeToHumanReadable(str) {
    return str[0].toUpperCase() + str.substring(1).replace(/_([a-z])/g, (_, chr) => ` ${chr.toUpperCase()}`);
  }

  function formatTimestamp(timestamp) {
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', { timeZone: 'UTC' });
  }

  const headers = ['Full Name', 'Action', 'Date/Time', 'Entity', 'Entity ID'];

  return (
    <>
      {isInitialLoading && <Loading />}
      {!isInitialLoading && (
        <>
          <FiltersContainer>
            <select ref={typeRef}>
              <option value="">Select Entity Type</option>
              {typeFilter.map(type => (
                <option key={type} value={type} selected={type === targetType}>
                  {type}
                </option>
              ))}
            </select>
            <input
              placeholder="Entity ID (ex: 9b0c8d00-e0a8-11ea-aa55-64bce07f70fd)"
              ref={idRef}
              label="Entity ID"
              margin="normal"
              style={{ minWidth: '350px' }}
              defaultValue={targetId}
            />
            <button onClick={handleFilterSubmit}>Filter</button>
            <button type="secondary" onClick={handleClearFilter}>
              Clear Filter
            </button>
          </FiltersContainer>
          <Table>
            <TableHead>
              {/* Table Headings */}
              <TableRow>{headers && headers.map(header => <TableCell key={header}>{header}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {data &&
                data.map(row => (
                  <>
                    <TableRow key={row._id} onClick={() => setSelectedAuditLog(row._id)} style={{ cursor: 'pointer' }}>
                      <TableCell>{row.full_name}</TableCell>
                      <TableCell>{snakeToHumanReadable(row.action)}</TableCell>
                      <TableCell>{formatTimestamp(row.timestamp)}</TableCell>
                      <TableCell>{snakeToHumanReadable(row.target_type)}</TableCell>
                      <TableCell>{row.target_id}</TableCell>
                    </TableRow>
                    {selectedAuditLog === row._id && (
                      <TableRow>
                        <TableCell colSpan={headers.length}>
                          <pre>{JSON.stringify(flattenObject(_.omit(row, payloadOmittedFields), '.'), null, 2)}</pre>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
            </TableBody>
          </Table>
        </>
      )}
      {pagination && (
        <TablePagination
          component="div"
          count={Number(pagination.total)}
          rowsPerPage={data.length}
          rowsPerPageOptions={[10, 20, 50, 100]}
          page={pagination.page}
          backIconButtonProps={{ 'aria-label': 'Previous Page' }}
          nextIconButtonProps={{ 'aria-label': 'Next Page' }}
          onChangePage={handleChangePage}
        />
      )}
    </>
  );
};

export default withRouter(AuditLogs);

const FiltersContainer = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem;
  align-items: center;
`;
